import { Module } from 'vuex';

import { itemStatementsService } from '@/services/item-statements/item-statements.service';
import { ItemStatement, Origin } from '@/services/item-statements/item-statements.types';
import { RootState } from '@/store';
import { Statement } from '@/services/statements/statements.types';

export interface ItemStatementState {
  itemStatementList: ItemStatement[];
  itemStatementListLoading: boolean;
}

const state: ItemStatementState = {
  itemStatementList: [],
  itemStatementListLoading: false
};

const options: Module<ItemStatementState, RootState> = {
  namespaced: true,
  state: () => state,
  actions: {
    getItemStatement: ({ commit, dispatch }, device_id: number): Promise<void> => {
      commit('itemStatementListLoading', true);
      return itemStatementsService
        .get(device_id)
        .then(itemStatementList => {
          itemStatementList && commit('itemStatementList', itemStatementList);
        })
        .catch(error => error && dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('itemStatementListLoading', false));
    },
    getItemStatementWithCancel: ({ commit, dispatch }, device_id: number): Promise<void> => {
      commit('itemStatementListLoading', true);
      return itemStatementsService
        .getWithCancel(device_id)
        .then(itemStatementList => itemStatementList && commit('itemStatementList', itemStatementList))
        .catch(async error => {
          if (error) dispatch('alert/pushError', error, { root: true });
          // Fallback for environments where with_cancel endpoint is unavailable.
          return itemStatementsService
            .get(device_id)
            .then(itemStatementList => itemStatementList && commit('itemStatementList', itemStatementList))
            .catch(getError => getError && dispatch('alert/pushError', getError, { root: true }));
        })
        .finally(() => commit('itemStatementListLoading', false));
    },
    createItemStatementList: ({ commit, dispatch }, items: ItemStatement[]): Promise<void> => {
      commit('itemStatementListLoading', true);
      return itemStatementsService
        .create(items)
        .catch(error => {
          return error && dispatch('alert/pushError', error, { root: true });
        })
        .finally(() => commit('itemStatementListLoading', false));
    },
    updateItemStatement: ({ commit, dispatch }, itemStatement: ItemStatement): Promise<void> => {
      commit('itemStatementListLoading', true);
      return itemStatementsService
        .update(itemStatement)
        .then(itemStatement => itemStatement && commit('UPDATE_ITEM', itemStatement))
        .then(() => dispatch('alert/pushSuccess', 'Modification sauvegardée !', { root: true }))
        .catch(error => error && dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('itemStatementListLoading', false));
    },
    deleteItemStatement: ({ commit, dispatch }, itemStatementId: number): Promise<void> => {
      // dispatch('removeItemStatement', itemStatementId);
      commit('itemStatementListLoading', true);
      return itemStatementsService
        .delete(itemStatementId)
        .then(itemStatement => itemStatement && commit('UPDATE_ITEM', itemStatement))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('itemStatementListLoading', false));
    },
    createItemStatementWithStatement: ({ commit }, options: { statement: Statement; origin: Origin }): void => {
      let id = 1;
      if (state.itemStatementList.map(o => o.id).length > 0)
        id = Math.max(...state.itemStatementList.map(o => o.id)) + 1;
      const rawPartsCode =
        options.statement.parts_code === 'battery' ? 'extract_battery' : options.statement.parts_code;
      const parts_code = rawPartsCode ? [rawPartsCode] : [];
      const itemStatement: ItemStatement = {
        id: id,
        item_id: null,
        statement: options.statement,
        statement_id: options.statement.id,
        origin: options.origin,
        name: options.statement.complete_name,
        parts_code: parts_code,
        item_statement_photos: [],
        selected_end: 0
      };
      commit('addItemStatement', itemStatement);
    },
    editItemStatements: ({ commit }, itemStatement: ItemStatement): void => {
      commit('UPDATE_ITEM', itemStatement);
    },
    removeItemStatement: ({ commit }, itemStatementId: number): void => {
      commit('removeItemStatement', itemStatementId);
    },
    clearItemStatementList: ({ commit, dispatch }, device_id: number): Promise<void> => {
      commit('itemStatementListLoading', true);
      return itemStatementsService
        .get(device_id)
        .then(itemStatementList => itemStatementList && commit('itemStatementList', itemStatementList))
        .catch(error => error && dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('itemStatementListLoading', false));
    },
    clearItemStatement({ commit }): void {
      commit('itemStatementList', []);
      commit('itemStatementListLoading', false);
    }
  },
  mutations: {
    itemStatementList: (state, itemStatementList: ItemStatement[]) => (state.itemStatementList = itemStatementList),
    addItemStatement: (state, itemStatement: ItemStatement) => state.itemStatementList.push(itemStatement),
    removeItemStatement: (state, itemStatement_id: number) =>
      (state.itemStatementList = state.itemStatementList.filter(itemStatement => itemStatement.id != itemStatement_id)),
    editItemStatement: (state, payload: { index; itemStatement }) =>
      (state.itemStatementList[payload.index] = payload.itemStatement),
    UPDATE_ITEM(state: ItemStatementState, item: ItemStatement) {
      const i = state.itemStatementList.findIndex(is => is.id === item.id);
      if (i !== -1) state.itemStatementList.splice(i, 1, item);
    },
    itemStatementListLoading: (state, loading: boolean) => (state.itemStatementListLoading = loading)
  },
  getters: {
    device: state => (state.itemStatementList[0] ? state.itemStatementList[0].item_id || null : null)
  }
};

export default options;
